import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  MoreVertical,
  MessageSquare,
  Eye,
  CalendarIcon,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import {
  format,
  isPast,
  startOfDay,
  isSameDay,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  addMonths,
  subMonths,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  getDay,
} from "date-fns";
import { useState, useEffect } from "react";
import moment from "moment";
import { appointment_status } from "@/models/appointment.model";
import { AppointmentChatService } from "@/services/appointment-chat.service";
import MainLayout from "@/components/layouts";
import { useAppointmentCalendar } from "@/hooks/useAppointmentCalendar";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { MonthPicker } from "@/components/ui/month-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type CalendarView = "day" | "week" | "month";

const CALENDAR_VIEW_STORAGE_KEY = "appointment-calendar-view";

export default function AppointmentCalendarPage() {
  // View state with localStorage persistence
  const [view, setView] = useState<CalendarView>(() => {
    const saved = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return (saved as CalendarView) || "month";
  });

  // Save view to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
  }, [view]);

  // Get current month and year
  const currentMonth = moment().toDate();
  const [currentViewDate, setCurrentViewDate] = useState(currentMonth);

  const {
    selectedDate,
    setSelectedDate,
    appointments,
    allAppointments,
    loading,
    error,
    statusFilter,
    setStatusFilter,
    hasAppointments,
    getAppointmentCount,
  } = useAppointmentCalendar({ maxAppointments: 200, layer: "subs", currentMonth: currentViewDate });

  // Get appointments for a specific date from all appointments
  const getAppointmentsForDate = (date: Date) => {
    let dayAppointments = allAppointments.filter((appointment) => isSameDay(moment(appointment.date).toDate(), date));

    // Apply status filter
    if (statusFilter !== "all") {
      dayAppointments = dayAppointments.filter((apt) => apt.status === statusFilter);
    }

    return dayAppointments;
  };

  const getStatusColor = (status: appointment_status) => {
    switch (status) {
      case appointment_status.pending:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case appointment_status.assigned:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case appointment_status.inprogress:
        return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case appointment_status.done:
        return "bg-green-100 text-green-800 border-green-200";
      case appointment_status.goback:
        return "bg-orange-100 text-orange-800 border-orange-200";
      case appointment_status.rescheduled:
        return "bg-purple-100 text-purple-800 border-purple-200";
      case appointment_status.inspected:
        return "bg-sky-100 text-sky-800 border-sky-200";
      case appointment_status.cancelled:
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  // Handle opening chat
  const handleOpenChat = async (appointmentId: string) => {
    try {
      const chatRoom = await AppointmentChatService.getAppointmentGroupChat(appointmentId);
      if (chatRoom) {
        window.location.href = `/messages/${chatRoom.id}`;
      }
    } catch (error) {
      console.error("Failed to open chat:", error);
    }
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (view === "day") {
      setCurrentViewDate(subDays(currentViewDate, 1));
      setSelectedDate(subDays(selectedDate, 1));
    } else if (view === "week") {
      const newDate = subWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    } else {
      // month
      const newMonth = subMonths(currentViewDate, 1);
      setCurrentViewDate(newMonth);
    }
  };

  const handleNext = () => {
    if (view === "day") {
      setCurrentViewDate(addDays(currentViewDate, 1));
      setSelectedDate(addDays(selectedDate, 1));
    } else if (view === "week") {
      const newDate = addWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    } else {
      // month
      const newMonth = addMonths(currentViewDate, 1);
      setCurrentViewDate(newMonth);
    }
  };

  // Handle date selection from dropdown (day view)
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setCurrentViewDate(date);
    setSelectedDate(date);
  };

  // Handle month/year selection from dropdown (week and month views)
  const handleMonthYearSelect = (date: Date | undefined) => {
    if (!date) return;
    if (view === "week") {
      // Jump to the week that contains the 1st day of the selected month
      const firstDayOfMonth = startOfMonth(date);
      const weekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }); // Monday
      setCurrentViewDate(weekStart);
      setSelectedDate(weekStart);
    } else {
      // month
      setCurrentViewDate(date);
    }
  };

  // Only update currentViewDate when navigating months/weeks, not when selecting dates
  // This prevents unnecessary refetches when just clicking on a date

  // Calculate week days (Mon-Sun)
  const getWeekDays = () => {
    const weekStart = startOfWeek(currentViewDate, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(currentViewDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  };

  // Calculate month days
  const getMonthDays = () => {
    const monthStart = startOfMonth(currentViewDate);
    const monthEnd = endOfMonth(currentViewDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstDayOfMonth = getDay(monthStart);
    const previousMonthDays = [];

    // Adjust for Monday start (1 = Monday, 0 = Sunday)
    const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    for (let i = 0; i < offset; i++) {
      previousMonthDays.push(null);
    }

    return [...previousMonthDays, ...daysInMonth];
  };

  // Get title based on view
  const getTitle = () => {
    if (view === "day") {
      return format(selectedDate, "EEEE, MMMM d, yyyy");
    } else if (view === "week") {
      return format(currentViewDate, "MMMM yyyy");
    } else {
      return format(currentViewDate, "MMMM yyyy");
    }
  };

  // Render Day View
  const renderDayView = () => {
    const dayAppointments = getAppointmentsForDate(selectedDate);

    return (
      <div className="space-y-4">
        {dayAppointments.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No appointments for {format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {dayAppointments.map((appointment) => (
              <div key={appointment.id} className="p-4 border rounded-lg hover:bg-accent transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Link to={`/appointments/${appointment.id}`} className="font-semibold hover:underline">
                        {appointment.property.name}
                      </Link>
                      <AppointmentStatusBadge status={appointment.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Unit {appointment.property.unit_number} • {appointment.service.name}
                    </p>
                    <p className="text-sm text-muted-foreground">{appointment.vendor?.name || "No vendor assigned"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => handleOpenChat(appointment.id)}>
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open Chat</TooltipContent>
                    </Tooltip>
                    <Link to={`/appointments/${appointment.id}`}>
                      <Button variant="ghost" size="icon">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDays = getWeekDays();

    return (
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day, index) => {
          if (!day) {
            return <div key={index} className="h-24" />;
          }

          const dayAppointments = getAppointmentsForDate(day);
          const appointmentCount = dayAppointments.length;
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, moment().toDate());
          const isPastDate = isPast(startOfDay(day)) && !isToday;

          return (
            <div
              key={day.toISOString()}
              className={cn(
                "min-h-[300px] border border-border rounded-lg p-1 cursor-pointer transition-colors hover:bg-muted/50",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary/70",
                isToday && !isSelected && "bg-muted",
                isPastDate && !isSelected && "opacity-50"
              )}
              onClick={() => setSelectedDate(day)}
            >
              <div className="flex flex-col h-full">
                <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                {appointmentCount > 0 && (
                  <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                    {dayAppointments.slice(0, 15).map((appointment) => (
                      <div
                        key={appointment.id}
                        className={cn("text-xs px-1 py-0.5 rounded truncate", getStatusColor(appointment.status))}
                      >
                        {appointment.property.name} - {appointment.service.name}
                      </div>
                    ))}
                    {appointmentCount > 15 && (
                      <div className="text-xs text-muted-foreground">+{appointmentCount - 15} more</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const allDays = getMonthDays();

    return (
      <>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {allDays.map((day, index) => {
            if (!day) {
              return <div key={index} className="min-h-[72px]" />;
            }

            const dayAppointments = getAppointmentsForDate(day);
            const appointmentCount = dayAppointments.length;
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, moment().toDate());
            const isPastDate = isPast(startOfDay(day)) && !isToday;

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "min-h-[72px] border border-border rounded-lg p-1 cursor-pointer transition-colors hover:bg-muted/50",
                  isSelected && "bg-primary text-primary-foreground hover:bg-primary/70",
                  isToday && !isSelected && "bg-muted",
                  isPastDate && !isSelected && "opacity-50"
                )}
                onClick={() => setSelectedDate(day)}
              >
                <div className="flex flex-col h-full">
                  <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                  {appointmentCount > 0 && (
                    <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                      {dayAppointments.slice(0, 3).map((appointment) => (
                        <div
                          key={appointment.id}
                          className={cn("text-xs px-1 py-0.5 rounded truncate", getStatusColor(appointment.status))}
                        >
                          {appointment.property.name} - {appointment.service.name}
                        </div>
                      ))}
                      {appointmentCount > 3 && (
                        <div className="text-xs text-muted-foreground">+{appointmentCount - 3} more</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const pageHeaderButtons = (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="lg">
            <Filter className="mr-2 h-4 w-4" />
            Filter: {statusFilter === "all" ? "All" : statusFilter.replace("_", " ")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setStatusFilter("all")}>All Appointments</DropdownMenuItem>
          {Object.values(appointment_status).map((status) => (
            <DropdownMenuItem key={status} onClick={() => setStatusFilter(status)}>
              {status.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Link to="/appointments">
        <Button variant="outline" size="lg">
          <CalendarIcon className="mr-2 h-4 w-4" />
          List View
        </Button>
      </Link>
    </>
  );

  if (loading) {
    return (
      <MainLayout
        pageHeader={{
          title: "Appointment Calendar",
          subtitle: "View and manage your appointments",
          additionalButtons: pageHeaderButtons,
        }}
      >
        <div className="space-y-6 container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-3">
              <Card>
                <CardHeader>
                  <CardTitle>Loading...</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-96 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout
        pageHeader={{
          title: "Appointment Calendar",
          subtitle: "View and manage your appointments",
          additionalButtons: pageHeaderButtons,
        }}
      >
        <Card>
          <CardContent className="flex items-center justify-center h-96">
            <div className="text-center">
              <p className="text-red-500 mb-4">Error loading appointments: {error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      pageHeader={{
        title: "Appointment Calendar",
        subtitle: "View and manage your appointments",
        additionalButtons: pageHeaderButtons,
      }}
    >
      <div className="space-y-6 container mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold text-lg hover:bg-transparent"
                        title={view === "day" ? format(selectedDate, "PPP") : format(currentViewDate, "MMM yyyy")}
                      >
                        <div className="flex items-center gap-2">
                          <CardTitle className="mb-0">{getTitle()}</CardTitle>
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start" side="bottom">
                      {view === "day" ? (
                        <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus />
                      ) : (
                        <MonthPicker selectedMonth={currentViewDate} onMonthSelect={handleMonthYearSelect} />
                      )}
                    </PopoverContent>
                  </Popover>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrevious}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleNext}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Select value={view} onValueChange={(value) => setView(value as CalendarView)}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Day</SelectItem>
                        <SelectItem value="week">Week</SelectItem>
                        <SelectItem value="month">Month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {view === "day" && renderDayView()}
                {view === "week" && (
                  <>
                    <div className="grid grid-cols-7 gap-1 mb-2">
                      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                        <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                          {day}
                        </div>
                      ))}
                    </div>
                    {renderWeekView()}
                  </>
                )}
                {view === "month" && renderMonthView()}
              </CardContent>
            </Card>
          </div>

          {/* Selected Date Appointments */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>{format(selectedDate, "EEEE, MMMM d, yyyy")}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  {getAppointmentCount(selectedDate)} appointment{getAppointmentCount(selectedDate) !== 1 ? "s" : ""}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {appointments.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No appointments for this date</p>
                ) : (
                  appointments.map((appointment) => (
                    <div key={appointment.id} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{appointment.property.name}</p>
                          <p className="text-xs text-muted-foreground">Unit {appointment.property.unit_number}</p>
                          <p className="text-xs text-muted-foreground">{appointment.service.name}</p>
                          {appointment.vendor && (
                            <p className="text-xs text-muted-foreground">Vendor: {appointment.vendor.name}</p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/appointments/${appointment.id}`}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleOpenChat(appointment.id)}>
                              <MessageSquare className="mr-2 h-4 w-4" />
                              Open Chat
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="flex items-center justify-between">
                        <AppointmentStatusBadge status={appointment.status} />
                        <span className="text-xs text-muted-foreground">
                          {format(moment(appointment.date).toDate(), "h:mm a")}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
